// components/EventSummary/DateAndLocation.js

import React from 'react';
import styled from 'styled-components';
import EventMap from '../EventMap';
import Typography from '../Typography';
import { theme } from '../../theme';
import LocationIcon from '../Icons/LocationIcon';
import { useTranslation } from 'react-i18next';
import { formatMonthDate, formatDayDate, formatEventDateRange } from '../../utils/dateUtils';

const DateAndLocation = ({
  date,
  location,
  shortLocation,
  mapCoordinates,
  containerBorderRadius, // Añadir nueva prop
  containerBorderColor,
  mapMaxHeight,
  mapMobileHeight,
  mapBorderRadius,
  mapPadding,
  mapMargin,
}) => {
  const { i18n } = useTranslation();

  // Usar las funciones de formateo de fechas con internacionalización
  const formattedMonthDate = formatMonthDate(date.startDate, i18n.language);
  const formattedDayDate = formatDayDate(date.startDate);

  // Format event date range according to requirements
  const eventDateRange = formatEventDateRange(date.startDate, date.endDate, i18n.language);

  return (
    <Container
      $containerBorderRadius={containerBorderRadius}
      $containerBorderColor={containerBorderColor}
    >
      <DateRow>
        <DateContainer>
          <MonthDate>
            <Typography
              $variant="overline-semibold"
              $textTransform="uppercase"
              color={theme.colors.defaultMain}
              $letterSpacing="2px"
            >
              {formattedMonthDate}
            </Typography>
          </MonthDate>
          <DayDate>
            <Typography
              $variant="body-1-bold"
              color={theme.colors.defaultMain}
            >
              {formattedDayDate}
            </Typography>
          </DayDate>
        </DateContainer>
        <DateText>
          {eventDateRange.singleLine ? (
            <Typography $variant="body-1-semibold" as="p">
              {eventDateRange.singleLine}
            </Typography>
          ) : (
            <>
              <Typography $variant="body-1-semibold" as="p">
                {eventDateRange.startLine}
              </Typography>
              <Typography $variant="body-2-semibold" color={theme.colors.defaultWeak} as="p">
                {eventDateRange.endLine}
              </Typography>
            </>
          )}
        </DateText>
      </DateRow>
      <LocationRow>
        <LocationContainer>
          <LocationIcon fill={theme.colors.defaultMain} height={24} width={24} />
        </LocationContainer>
        <LocationAddress>
          <Typography $variant="body-1-semibold" as="p">
            {location}
          </Typography>
          <Typography $variant="body-2-medium" as="p" color={theme.colors.defaultWeak}>
            {shortLocation}
          </Typography>
        </LocationAddress>
      </LocationRow>
      <EventMap
        lat={mapCoordinates.lat}
        lng={mapCoordinates.lng}
        maxHeight={mapMaxHeight || '200px'}
        mobileHeight={mapMobileHeight || '120px'}
        borderRadius={mapBorderRadius || '0px'}
        padding={mapPadding || '0px'}
        margin={mapMargin || '0px'}
      />
    </Container>
  );
};

export default DateAndLocation;

// Styled Components
const Container = styled.div`
  border-radius: ${({ theme, $containerBorderRadius }) => $containerBorderRadius || theme.sizing.sm}; // Usar prop o valor del tema
  border: 1px solid ${({ theme, $containerBorderColor }) => $containerBorderColor || theme.border.defaultSubtle}; // Usar prop o valor del tema
  background-color: ${({ theme }) => theme.fill.defaultMain};
  overflow: hidden;
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-bottom: ${({ theme }) => theme.sizing.xs};
  }
`;

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 48px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.fill.defaultSubtle};
  overflow: hidden;
`;

const MonthDate = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  background-color: #EFEFEF;
`;

const DayDate = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 28px;
`;


const LocationRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: ${({ theme }) => theme.sizing.xs};
    padding-bottom: ${({ theme }) => theme.sizing.xs};
  }
`;

const LocationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 52px;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  flex-shrink: 0;
`;

const LocationAddress = styled.div`
`;

const DateText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xxs};
  flex-grow: 1;
`;
